import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  acceptWebTerms,
  applyWebPromocode,
  createDeviceAddonCheckout,
  createSubscriptionCheckout,
  fetchBootstrap,
  fetchOrderStatus,
  fetchWebBootstrap,
  fetchWebReferral,
  isTelegramMiniApp,
  logoutWebSession,
  openExternalPayment,
  prepareTelegramWebApp,
  startTelegramWebLogin,
  type BootstrapData,
  type WebPublicInfo,
  type WebReferralInfo,
} from "./api";
import { plans as mockPlans, createMockSnapshot } from "./mock";
import type { AppSnapshot, DeviceAddonConfig, Plan, Screen, Tab, VpnState } from "./types";
import { BottomNav, Header } from "./ui";
import {
  AboutScreen,
  AddDevicesScreen,
  DevicePaymentScreen,
  HomeScreen,
  InstructionScreen,
  MoreScreen,
  PlanScreen,
  ProfileScreen,
  PromoScreen,
  ReferralScreen,
  SuccessScreen,
  SupportScreen,
  TermsScreen,
  VpnScreen,
} from "./screens";

function initialStateFromUrl(): VpnState {
  const state = new URLSearchParams(window.location.search).get("state");
  if (state === "expired" || state === "never_had" || state === "active") return state;
  return "active";
}

function messageFor(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.userMessage
    ? error.userMessage
    : fallback;
}

const subflowScreens = new Set<Screen>([
  "renew",
  "buy",
  "add-devices",
  "device-payment",
  "instruction",
  "promo",
  "referral",
  "support",
  "about",
  "purchase-success",
  "renew-success",
]);

export default function App() {
  const telegramMode = isTelegramMiniApp();
  const query = new URLSearchParams(window.location.search);
  const demoMode = import.meta.env.DEV && query.get("demo") === "1";
  const browserMode = !telegramMode && !demoMode;
  const loginError = browserMode && query.get("auth_error") === "telegram";

  const [snapshot, setSnapshot] = useState<AppSnapshot>(() =>
    createMockSnapshot(initialStateFromUrl()),
  );
  const [availablePlans, setAvailablePlans] = useState<Plan[]>(
    demoMode ? mockPlans : [],
  );
  const [selectedPlan, setSelectedPlan] = useState<Plan>(mockPlans[1]);
  const [deviceAddon, setDeviceAddon] = useState<DeviceAddonConfig>({
    pricePerDeviceRub: 50,
    maxLimit: 5,
  });
  const [screen, setScreen] = useState<Screen>("home");
  const [menuReturnScreen, setMenuReturnScreen] = useState<Screen>("home");
  const [signedIn, setSignedIn] = useState(telegramMode || demoMode);
  const [termsAccepted, setTermsAccepted] = useState(telegramMode || demoMode);
  const [publicInfo, setPublicInfo] = useState<WebPublicInfo | null>(null);
  const [referral, setReferral] = useState<WebReferralInfo | null>(null);
  const [deviceQuantity, setDeviceQuantity] = useState(1);
  const [toastText, setToastText] = useState<string | null>(null);
  const [apiConnected, setApiConnected] = useState(false);
  const [loading, setLoading] = useState(!demoMode);
  const [pendingOrder, setPendingOrder] = useState<{
    id: number;
    kind: "subscription" | "device_addon";
  } | null>(null);

  const activeTab: Tab = useMemo(() => {
    if (
      screen === "home" ||
      screen === "vpn" ||
      screen === "profile" ||
      screen === "more"
    ) {
      return screen;
    }
    if (
      screen === "instruction" ||
      screen === "promo" ||
      screen === "referral" ||
      screen === "support" ||
      screen === "about"
    ) {
      return "more";
    }
    return "vpn";
  }, [screen]);

  const showBottomNav =
    !subflowScreens.has(screen) ||
    ["instruction", "promo", "referral", "support", "about"].includes(screen);

  const toast = useCallback((message: string) => {
    setToastText(message);
    window.setTimeout(() => setToastText(null), 1800);
  }, []);

  const applyBootstrap = useCallback((data: BootstrapData) => {
    setSnapshot(data.snapshot);
    setAvailablePlans(data.plans);
    setSelectedPlan((current) => {
      const same = data.plans.find(
        (plan) => plan.backendId === current.backendId,
      );
      return same ?? data.plans[0] ?? current;
    });
    setDeviceAddon(data.deviceAddon);
  }, []);

  const loadRealData = useCallback(async () => {
    if (demoMode) {
      setSignedIn(true);
      setTermsAccepted(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      if (telegramMode) {
        const data = await fetchBootstrap();
        if (!data) throw new Error("missing_bootstrap");
        applyBootstrap(data);
        setSignedIn(true);
        setTermsAccepted(true);
        setApiConnected(true);
        return;
      }

      const data = await fetchWebBootstrap();
      if (!data) {
        setSignedIn(false);
        setTermsAccepted(false);
        setApiConnected(false);
        setPublicInfo(null);
        setReferral(null);
        return;
      }

      applyBootstrap(data);
      setSignedIn(true);
      setTermsAccepted(data.termsAccepted);
      setPublicInfo(data.publicInfo);
      setApiConnected(true);
    } catch (error) {
      if (browserMode && error instanceof ApiError && error.status === 401) {
        setSignedIn(false);
        setTermsAccepted(false);
        setApiConnected(false);
      } else {
        setApiConnected(false);
        toast(messageFor(error, "Не удалось загрузить аккаунт"));
      }
    } finally {
      setLoading(false);
    }
  }, [applyBootstrap, browserMode, demoMode, telegramMode, toast]);

  useEffect(() => {
    if (telegramMode) prepareTelegramWebApp();
    void loadRealData();
  }, [loadRealData, telegramMode]);

  useEffect(() => {
    if (!apiConnected) return;
    const refresh = () => {
      if (document.visibilityState === "visible") void loadRealData();
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [apiConnected, loadRealData]);

  useEffect(() => {
    if (
      !browserMode ||
      !signedIn ||
      !termsAccepted ||
      screen !== "referral"
    ) {
      return;
    }

    let cancelled = false;
    void fetchWebReferral()
      .then((data) => {
        if (!cancelled) setReferral(data);
      })
      .catch(() => {
        if (!cancelled) setReferral(null);
      });

    return () => {
      cancelled = true;
    };
  }, [browserMode, screen, signedIn, termsAccepted]);

  const go = useCallback((next: Screen) => {
    setScreen(next);
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(".scroll-area")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, []);

  useEffect(() => {
    if (!apiConnected || !pendingOrder) return;

    let cancelled = false;
    let timer: number | undefined;

    const check = async () => {
      try {
        const status = await fetchOrderStatus(pendingOrder.id);
        if (cancelled) return;

        if (status.status === "paid") {
          await loadRealData();
          setPendingOrder(null);
          toast(
            pendingOrder.kind === "device_addon"
              ? "Устройства добавлены"
              : "Оплата подтверждена",
          );
          go("vpn");
          return;
        }

        if (status.status === "cancelled" || status.status === "failed") {
          setPendingOrder(null);
          toast("Оплата не завершена");
          return;
        }
      } catch {
        // Webhook may still be processing; retry shortly.
      }

      if (!cancelled) timer = window.setTimeout(check, 2500);
    };

    timer = window.setTimeout(check, 1200);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [apiConnected, go, loadRealData, pendingOrder, toast]);

  const handleMenuToggle = () => {
    if (screen === "more") {
      go(menuReturnScreen);
      return;
    }
    setMenuReturnScreen(screen);
    go("more");
  };

  const handleBottomTab = (tab: Tab) => {
    if (tab === "more" && screen !== "more") {
      setMenuReturnScreen(screen);
    }
    go(tab);
  };

  const completePurchase = async () => {
    if (apiConnected && selectedPlan.backendId) {
      try {
        const checkout = await createSubscriptionCheckout(selectedPlan.backendId);
        setPendingOrder({ id: checkout.order_id, kind: "subscription" });
        toast("Открываем оплату…");
        openExternalPayment(checkout.payment_url);
      } catch (error) {
        toast(messageFor(error, "Не удалось открыть оплату"));
      }
      return;
    }

    if (!demoMode) {
      toast("Аккаунт недоступен. Попробуйте обновить приложение.");
      return;
    }

    setSnapshot((current) => ({
      ...current,
      vpn: {
        ...createMockSnapshot("active").vpn,
        lastPlanLabel: selectedPlan.label,
        daysLeft: selectedPlan.days,
      },
    }));
    go("purchase-success");
  };

  const completeRenewal = async () => {
    if (apiConnected && selectedPlan.backendId) {
      try {
        const checkout = await createSubscriptionCheckout(selectedPlan.backendId);
        setPendingOrder({ id: checkout.order_id, kind: "subscription" });
        toast("Открываем оплату…");
        openExternalPayment(checkout.payment_url);
      } catch (error) {
        toast(messageFor(error, "Не удалось открыть оплату"));
      }
      return;
    }

    if (!demoMode) {
      toast("Аккаунт недоступен. Попробуйте обновить приложение.");
      return;
    }

    setSnapshot((current) => ({
      ...current,
      vpn: {
        ...current.vpn,
        state: "active",
        expiresAt: "27.08.2026",
        daysLeft: selectedPlan.days,
        lastPlanLabel: selectedPlan.label,
      },
    }));
    go("renew-success");
  };

  const completeDevicePayment = async () => {
    if (apiConnected) {
      try {
        const checkout = await createDeviceAddonCheckout(
          deviceQuantity,
          snapshot.vpn.subscriptionId,
        );
        setPendingOrder({ id: checkout.order_id, kind: "device_addon" });
        toast("Открываем оплату…");
        openExternalPayment(checkout.payment_url);
      } catch (error) {
        toast(messageFor(error, "Не удалось открыть оплату"));
      }
      return;
    }

    if (!demoMode) {
      toast("Аккаунт недоступен. Попробуйте обновить приложение.");
      return;
    }

    setSnapshot((current) => ({
      ...current,
      vpn: {
        ...current.vpn,
        deviceLimit: current.vpn.deviceLimit + deviceQuantity,
      },
    }));
    toast("Устройства добавлены");
    go("vpn");
  };

  const handleLogin = () => {
    if (browserMode) {
      startTelegramWebLogin();
      return;
    }
    setSignedIn(true);
  };

  const handleLogout = async () => {
    if (browserMode) {
      try {
        await logoutWebSession();
      } catch (error) {
        toast(messageFor(error, "Не удалось выйти из аккаунта"));
        return;
      }
      setSignedIn(false);
      setTermsAccepted(false);
      setApiConnected(false);
      setPublicInfo(null);
      setReferral(null);
      setPendingOrder(null);
      setScreen("profile");
      return;
    }

    setSignedIn(false);
    setScreen("profile");
  };

  const handleAcceptTerms = async () => {
    try {
      await acceptWebTerms();
      setTermsAccepted(true);
      await loadRealData();
      toast("Условия приняты");
    } catch (error) {
      toast(messageFor(error, "Не удалось сохранить согласие"));
    }
  };

  const handlePromo = async (code: string) => {
    if (!browserMode) {
      toast("Промокод применён");
      return;
    }

    try {
      await applyWebPromocode(code);
      await loadRealData();
      toast("Промокод применён");
    } catch (error) {
      toast(messageFor(error, "Не удалось применить промокод"));
    }
  };

  const copyReferral = async () => {
    try {
      let data = referral;
      if (browserMode && !data) {
        data = await fetchWebReferral();
        setReferral(data);
      }
      const link =
        data?.link || "https://t.me/AIDAHAR_VPN_bot?start=ref_1";
      await navigator.clipboard.writeText(link);
      toast("Реферальная ссылка скопирована");
    } catch (error) {
      toast(messageFor(error, "Не удалось скопировать ссылку"));
    }
  };

  const handleHeaderBack = () => {
    if (screen === "device-payment") return go("add-devices");
    if (screen === "buy") {
      return go(snapshot.vpn.state === "never_had" ? "home" : "vpn");
    }
    if (screen === "renew" || screen === "add-devices") return go("vpn");
    if (screen === "purchase-success" || screen === "renew-success") {
      return go("vpn");
    }
    return go(activeTab);
  };

  const renderScreen = () => {
    if (loading) {
      return (
        <section className="loading-state">
          <div className="loading-dot" />
          <strong>Загружаем AIDAHAR VPN</strong>
          <p>Проверяем подписку и аккаунт…</p>
        </section>
      );
    }

    if (browserMode && !signedIn) {
      return (
        <ProfileScreen
          data={snapshot}
          signedIn={false}
          onLogin={handleLogin}
          onLogout={() => undefined}
          go={go}
          loginError={loginError}
        />
      );
    }

    if (browserMode && signedIn && !termsAccepted) {
      return (
        <TermsScreen
          legal={publicInfo?.legal ?? []}
          onAccept={handleAcceptTerms}
        />
      );
    }

    if (!apiConnected && !demoMode) {
      return (
        <section className="loading-state">
          <strong>Не удалось загрузить аккаунт</strong>
          <p>
            {telegramMode
              ? "Проверьте подключение и повторите попытку."
              : "Сессия есть, но данные аккаунта сейчас недоступны."}
          </p>
          <button type="button" onClick={() => void loadRealData()}>
            Повторить
          </button>
        </section>
      );
    }

    switch (screen) {
      case "home":
        return <HomeScreen data={snapshot} go={go} />;
      case "vpn":
        return <VpnScreen data={snapshot} go={go} toast={toast} />;
      case "profile":
        return (
          <ProfileScreen
            data={snapshot}
            signedIn={signedIn}
            onLogin={handleLogin}
            onLogout={() => void handleLogout()}
            go={go}
          />
        );
      case "more":
        return <MoreScreen signedIn={signedIn} go={go} />;
      case "renew":
        return (
          <PlanScreen
            mode="renew"
            plans={availablePlans}
            selected={selectedPlan}
            setSelected={setSelectedPlan}
            onSubmit={completeRenewal}
          />
        );
      case "buy":
        return (
          <PlanScreen
            mode="buy"
            plans={availablePlans}
            selected={selectedPlan}
            setSelected={setSelectedPlan}
            onSubmit={completePurchase}
          />
        );
      case "add-devices":
        return (
          <AddDevicesScreen
            currentLimit={snapshot.vpn.deviceLimit}
            maxLimit={deviceAddon.maxLimit}
            pricePerDeviceRub={deviceAddon.pricePerDeviceRub}
            quantity={deviceQuantity}
            setQuantity={(value) => {
              const allowed = Math.max(
                0,
                deviceAddon.maxLimit - snapshot.vpn.deviceLimit,
              );
              if (value > allowed) {
                toast("Максимум: " + deviceAddon.maxLimit + " устройств");
                return;
              }
              setDeviceQuantity(value);
            }}
            onContinue={() => go("device-payment")}
          />
        );
      case "device-payment":
        return (
          <DevicePaymentScreen
            quantity={deviceQuantity}
            pricePerDeviceRub={deviceAddon.pricePerDeviceRub}
            onPay={completeDevicePayment}
          />
        );
      case "instruction":
        return (
          <InstructionScreen
            go={go}
            instructionUrl={publicInfo?.instruction.url}
          />
        );
      case "promo":
        return <PromoScreen onApply={handlePromo} />;
      case "referral":
        return (
          <ReferralScreen
            onCopy={() => void copyReferral()}
            signupBonusDays={referral?.signup_bonus_days}
            paidBonusDays={referral?.paid_bonus_days}
          />
        );
      case "support":
        return (
          <SupportScreen
            go={go}
            supportUrl={publicInfo?.support.url}
          />
        );
      case "about":
        return (
          <AboutScreen
            data={snapshot}
            aboutText={publicInfo?.about}
          />
        );
      case "purchase-success":
        return <SuccessScreen kind="purchase" onContinue={() => go("vpn")} />;
      case "renew-success":
        return <SuccessScreen kind="renew" onContinue={() => go("vpn")} />;
      default:
        return null;
    }
  };

  const navigationReady =
    !loading &&
    (demoMode ||
      (signedIn && (!browserMode || termsAccepted) && apiConnected));

  return (
    <main className={browserMode ? "app-shell web-shell" : "app-shell"}>
      <div className={browserMode ? "phone-frame web-frame" : "phone-frame"}>
        <div className="fixed-background" aria-hidden="true">
          <img
            className="khokhloma-top"
            src="/khokhloma-top@4x.png?v=4"
            alt=""
          />
          <img
            className="khokhloma-bottom"
            src="/khokhloma-bottom@4x.png?v=4"
            alt=""
          />
        </div>

        <div className="fixed-header">
          <img
            className="brand-strip"
            src="/aidahar-brand-strip@4x.png?v=4"
            alt=""
          />
          <Header
            onMenu={
              navigationReady && !subflowScreens.has(screen)
                ? handleMenuToggle
                : undefined
            }
            onBack={
              navigationReady && subflowScreens.has(screen)
                ? handleHeaderBack
                : undefined
            }
            menuOpen={navigationReady && screen === "more"}
          />
        </div>

        <div className="scroll-area">
          <div
            className={
              showBottomNav && navigationReady
                ? "scroll-stack has-nav"
                : "scroll-stack"
            }
          >
            <div
              className={
                showBottomNav && navigationReady
                  ? "content has-bottom-nav"
                  : "content"
              }
            >
              <div
                className={
                  "screen-enter screen-" +
                  screen +
                  " vpn-" +
                  snapshot.vpn.state
                }
                key={
                  browserMode && !signedIn
                    ? "web-login"
                    : browserMode && !termsAccepted
                      ? "web-terms"
                      : screen
                }
              >
                {renderScreen()}
              </div>
            </div>
          </div>
        </div>

        {showBottomNav && navigationReady && (
          <BottomNav active={activeTab} onTab={handleBottomTab} />
        )}
        {toastText && <div className="toast">{toastText}</div>}
      </div>
    </main>
  );
}
